/**
 * TagInput
 *
 * Shared Content Library control for editing an asset's loose, admin-defined
 * tags. Renders the current tags as removable chips and an input that, as the
 * admin types, suggests existing tags (own + shared) via the shared tag API —
 * lowering the risk of near-duplicate typos.
 *
 * Tags are free-form: pressing Enter or comma commits whatever was typed;
 * clicking a suggestion commits that tag; Backspace on an empty input removes
 * the last chip. Styling composes existing utilities + the `.badge` chip class
 * (no bespoke CSS).
 */

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { suggestTags } from '../tagsApi';

export interface TagInputHandle {
  /**
   * Commits any pending draft text as a tag (like pressing Enter) and returns
   * the tag string that was added, or null if the draft was empty or a dupe.
   * The caller can use the return value synchronously without waiting for a
   * React state update.
   */
  flush: () => string | null;
}

interface TagInputProps {
  /** Current tags. */
  tags: string[];
  /** Called with the next tags array on any add/remove. */
  onChange: (tags: string[]) => void;
  /** DOM id for the input (label association). */
  id: string;
  /** Optional field label. */
  label?: string;
  /** Disable all editing (e.g. read-only shared assets). */
  disabled?: boolean;
  placeholder?: string;
}

export const TagInput = forwardRef<TagInputHandle, TagInputProps>(function TagInput(
  { tags, onChange, id, label, disabled = false, placeholder }: TagInputProps,
  ref,
) {
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced type-ahead lookup; skip tags already applied.
  useEffect(() => {
    const query = draft.trim();
    if (disabled || query === '') {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      const found = await suggestTags(query);
      const lowerExisting = new Set(tags.map((t) => t.toLowerCase()));
      setSuggestions(found.filter((t) => !lowerExisting.has(t.toLowerCase())));
    }, 200);
    return () => clearTimeout(handle);
  }, [draft, tags, disabled]);

  const addTag = useCallback(
    (raw: string) => {
      const tag = raw.trim();
      if (!tag) return;
      if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
        setDraft('');
        return;
      }
      onChange([...tags, tag]);
      setDraft('');
      setSuggestions([]);
    },
    [tags, onChange],
  );

  useImperativeHandle(ref, () => ({
    flush: () => {
      const tag = draft.trim();
      if (!tag) return null;
      if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
        setDraft('');
        return null;
      }
      onChange([...tags, tag]);
      setDraft('');
      setSuggestions([]);
      return tag;
    },
  }), [draft, tags, onChange]);

  const removeTag = useCallback(
    (tag: string) => {
      onChange(tags.filter((t) => t !== tag));
    },
    [tags, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
        e.preventDefault();
        addTag(draft);
      } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
        removeTag(tags[tags.length - 1]);
      }
    },
    [draft, tags, addTag, removeTag],
  );

  return (
    <div className="field">
      {label && <label htmlFor={id}>{label}</label>}

      <div className="d-flex flex-wrap gap-2xs align-items-center">
        {tags.map((tag) => (
          <span key={tag} className="badge badge--pill d-inline-flex align-items-center gap-2xs">
            {tag}
            {!disabled && (
              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                aria-label={`Remove tag ${tag}`}
                onClick={() => removeTag(tag)}
                style={{ padding: '0 6px', lineHeight: 1 }}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>

      <div className="position-relative">
        <input
          id={id}
          type="text"
          className="input"
          value={draft}
          disabled={disabled}
          placeholder={placeholder ?? 'Add a tag and press Enter'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Delay so a suggestion click registers before the list unmounts.
            blurTimer.current = setTimeout(() => setShowSuggestions(false), 150);
          }}
          autoComplete="off"
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            className="position-absolute w-100 bg-space bg-o-50 border-md border-blue-light rounded-sm overflow-y-auto"
            style={{ top: '100%', left: 0, zIndex: 20, maxHeight: 180 }}
          >
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="btn btn--outline btn--slate w-100 text-start"
                onMouseDown={() => {
                  // onMouseDown fires before blur so the value is captured.
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                  addTag(s);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
